/**
 * Admin Suite App Layout
 *
 * Persistent sidebar navigation layout for all admin routes.
 * Grouped navigation items and section dividers.
 *
 * @see BL-049.2 - Admin suite sidebar navigation
 */

import { Outlet, NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  FileCheck,
  Scale,
  Wallet,
  Coins,
  Flame,
  Lock,
  Clock,
  Activity,
  Shield,
  UserCog,
  CalendarDays,
  PenSquare,
  LogOut,
  BookOpen,
  ArrowRightLeft,
  Award,
  ShoppingCart,
  Receipt,
  Building2,
  UserPlus,
  ClipboardCheck,
  MailPlus,
} from 'lucide-react';
import { SuiteSwitcher } from '@hello-world-dao/ui';
import { SwitchToFosButton } from './SwitchToFosButton';
import { clearIdentityJwt } from '@/services/identity-jwt-store';
import {
  logoutIdentitySession,
  buildLoginFrontdoorUrl,
} from '@/services/identity-service-client';

/** Operations navigation group */
const operationsNav = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Members', href: '/members', icon: UserCheck },
  { name: 'KYC', href: '/kyc', icon: FileCheck },
  { name: 'Tax Forms', href: '/tax-forms', icon: Receipt },
  { name: 'Property Triage', href: '/property-triage', icon: ClipboardCheck },
  { name: 'Governance', href: '/governance', icon: Scale },
  { name: 'Orgs', href: '/orgs/new', icon: Building2 },
  { name: 'Add Member', href: '/orgs/members/new', icon: UserPlus },
  { name: 'Treasury', href: '/treasury', icon: Wallet },
  { name: 'Sub-Accounts', href: '/treasury/sub-accounts', icon: BookOpen },
  { name: 'Transfers', href: '/treasury/transfers', icon: ArrowRightLeft },
  { name: 'Patronage', href: '/treasury/patronage', icon: Award },
  { name: 'Tokens', href: '/tokens', icon: Coins },
  { name: 'Held Burns', href: '/held-burns', icon: Flame },
  { name: 'LP Locks', href: '/lp-locks', icon: Lock },
  { name: 'Vesting', href: '/vesting', icon: Clock },
  { name: 'Fee Schedule', href: '/marketplace/fees', icon: ShoppingCart },
  { name: 'Transactions', href: '/marketplace/transactions', icon: Receipt },
  { name: 'Monitoring', href: '/monitoring', icon: Activity },
];

/** Content & Access navigation group */
const contentNav = [
  { name: 'Tester Invites', href: '/uat-invites', icon: MailPlus },
  { name: 'Moderation', href: '/moderation', icon: Shield },
  { name: 'Roles', href: '/roles', icon: UserCog },
  { name: 'Events', href: '/events', icon: CalendarDays },
  { name: 'Blog', href: '/blog', icon: PenSquare },
];

/**
 * Handle logout: revoke BOTH sessions (the legacy oracle-bridge cookie AND the
 * identity-service refresh-token family + httpOnly cookie), clear the in-memory
 * identity JWT + localStorage, then redirect to the hosted login front-door.
 * Best-effort — errors are caught and the redirect proceeds regardless
 * (uid-001-suite-login-frontdoor).
 */
async function handleLogout() {
  const oracleUrl = import.meta.env.VITE_ORACLE_BRIDGE_URL;
  if (oracleUrl) {
    try {
      await fetch(`${oracleUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort — continue with redirect
    }
  }
  // Revoke the identity-service refresh family + clear its httpOnly cookie, and
  // drop the in-memory access JWT so no stale bearer survives the redirect.
  await logoutIdentitySession();
  clearIdentityJwt();
  localStorage.removeItem('user_data');
  window.location.href = buildLoginFrontdoorUrl();
}

export function AppLayout() {
  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Top header with title and suite switcher — AC3 */}
      <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6">
        <h1 className="text-xl font-bold">DAO Admin</h1>
        <div className="flex items-center gap-4">
          {/* PLATFORM-003.2 — cross-domain nav to FounderyOS */}
          <SwitchToFosButton />
          <SuiteSwitcher
            currentSuite="admin"
            suiteUrls={{
              portal: import.meta.env.VITE_DAO_FRONTEND_URL,
              governance: import.meta.env.VITE_GOVERNANCE_SUITE_URL,
              otterCamp: import.meta.env.VITE_OTTER_CAMP_URL,
            }}
          />
        </div>
      </header>

      {/* AC7: flex layout with independent scrolling */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — AC1, AC2 */}
        <aside className="flex w-48 flex-col border-r border-border bg-card">
          <div className="flex h-16 items-center px-6 border-b border-border">
            <div className="text-xl font-bold">Navigation</div>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Operations group */}
            {operationsNav.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}

            {/* Section divider — AC1 */}
            <div className="my-2 border-t border-border" role="separator" />

            {/* Content & Access group */}
            {contentNav.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Logout footer — AC4 */}
          <div className="p-4 border-t border-border">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </aside>

        {/* Main content — AC7: scrolls independently */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
